import React from 'react'
import { useDispatch } from 'react-redux'
import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { ILocationResult } from './models/locationResultModel'
import { Theme } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { mapAddressAutocompleteToLocationResults } from './models/locationResultMapper'
import { IAddressAutocomplete, IAddressSearchResolveResult } from '@cbgms/api/modules/miscellaneous/address/search'
import { EmptyAddress } from './models/locationSearchAddressResultModel'
import { useDebouncedCallback } from 'use-debounce'
import { Timing } from '@cbgms/base/utils/timing'
import { AsyncAction } from '@cbgms/base/redux/async-action'
import { GETArrayResponse, GETResponse } from '@cbgms/api'
import { useTranslation } from 'react-i18next'
import { inputBaseClasses } from '@mui/material/InputBase'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 'auto',
      display: 'block'
    },
    option: {
      fontSize: 15,
      '& > span': {
        margin: theme.spacing(2),
        marginRight: 10,
        fontSize: 18,
        width: 240
      }
    },
    field: {
      [`& .${inputBaseClasses.input}`]: {
        border: 'unset !important' // styling collision with semantic causes a double border
      }
    }
  })
)

export interface AddressSearchActions {
  // actions should be passed down into this component, this is reused both gms-fe and support
  AddressSearchAutocomplete: (country: string, searchString: string) => AsyncAction<GETArrayResponse<IAddressAutocomplete>>
  AddressSearchByIdAutocomplete: (country: string, id: string) => AsyncAction<GETArrayResponse<IAddressAutocomplete>>
  AddressSearchResolve: (country: string, searchString: string) => AsyncAction<GETResponse<IAddressSearchResolveResult>>
}
interface IAddressSearchProps {
  name?: string
  label?: string
  required?: boolean
  validate?: any
  disabled?: boolean
  fullWidth?: boolean
  country: string
  onChange: (resolvedAddress: IAddressSearchResolveResult) => void
  actions: AddressSearchActions
}

const AddressSearch: React.FC<IAddressSearchProps> = ({ name, label, required, disabled, fullWidth, country, onChange, actions }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [foundLocations, setFoundLocations] = React.useState<ILocationResult[]>([])
  const [open, setOpen] = React.useState(false)

  const search = async (searchTerm: string) => {
    if (searchTerm.length > 2) {
      const response = await dispatch(actions.AddressSearchAutocomplete(country, searchTerm))
      const locations = mapAddressAutocompleteToLocationResults(response.Data) || []
      locations.sort((l1, l2) => {
        if (l1.singleResult && !l2.singleResult) {
          return -1
        } else if (l1.singleResult === l2.singleResult) {
          if (l1.text < l2.text) return -1
        }
        return 1
      })
      setFoundLocations(locations)
    } else {
      setFoundLocations([])
    }
  }

  const handleSearch = useDebouncedCallback(search, Timing.keyboardInput)

  const resolveAutocompleResult = async (_: any, input: ILocationResult | null, reason: AutocompleteChangeReason) => {
    // call backend to resolve
    if (input?.singleResult) {
      setOpen(false) // Close the autocomplete immediately, because this gives a better UX.

      const resolveResponse = await dispatch(actions.AddressSearchResolve(country, input.text))
      const resolveResult = resolveResponse.Data
      onChange(resolveResult)
      return
    }

    if (input?.id) {
      // not a single result -> fetch new autocomplete results based on id
      const response = await dispatch(actions.AddressSearchByIdAutocomplete(country, input.id))
      const result = mapAddressAutocompleteToLocationResults(response.Data)
      setFoundLocations(result)
    }
    if (reason === 'clear') {
      setFoundLocations([])
      onChange(EmptyAddress())
    }
  }

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(_, reason) => {
        if (reason !== 'selectOption') setOpen(false)
      }}
      options={foundLocations || ([] as ILocationResult[])}
      getOptionLabel={option => `${option.text}${option.singleResult ? '' : ' >'}`}
      disabled={disabled}
      fullWidth={fullWidth}
      onChange={resolveAutocompleResult}
      filterOptions={x => x} // This is necessary because we load data while typing. See Material UI docs for vNext.
      size='small'
      classes={{ root: classes.root, option: classes.option }}
      style={{ height: 30 }}
      noOptionsText={t('global.no_options')}
      renderInput={params => (
        <TextField
          className={classes.field}
          {...params}
          name={name}
          label={label}
          placeholder={t('global.type_to_search')}
          required={required}
          variant='outlined'
          onChange={event => handleSearch(event.target.value)}
          autoComplete='new-password' /* disable autocomplete with  'new-password' */
        />
      )}
    />
  )
}

export default AddressSearch
